import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useFeature } from 'flagged';
import CloseButton from '@/components/common/CloseButton';
import FeatureFlags from '@/constants/system/FeatureFlags';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import { useQueryProvider } from '@/modules/QueryProvider';
import useSavedFilters, {
  type SavedFilterType,
} from '../hooks/useSavedFilters';
import type ConstituentSearchFilterParams from '../types/ConstituentSearchFilterParams';

function SavedFiltersSelect({
  className = '',
  onChange = () => {},
}: {
  className?: string;
  onChange?: (e: SavedFilterType | null) => void;
}) {
  const hasColumnSelection = useFeature(
    FeatureFlags.CONSTITUENT_COLUMN_SELECTION,
  );
  const { user, isOrgAdmin } = useCurrentUser();

  const { openDialog, closeDialog } = useDialogManager();

  const {
    clearQuery,
    filters: queryFilters,
    updateQuery,
  } = useQueryProvider<ConstituentSearchFilterParams>();
  const [searchQuery, setSearchQuery] = React.useState(
    queryFilters.filterName || '',
  );

  const { removeFilter, savedFilters } = useSavedFilters();

  const selectedFilter = React.useMemo(
    () =>
      (savedFilters || []).find(
        ({ filterName }) => filterName === queryFilters.filterName,
      ),
    [queryFilters, savedFilters],
  );

  const handleChange = (
    e: React.SyntheticEvent,
    value: SavedFilterType | null,
    reason: string,
  ) => {
    if (reason === 'clear') {
      clearQuery();
    } else {
      let update: Partial<ConstituentSearchFilterParams> = {
        filterName: undefined,
      };
      if (value) {
        const { advancedFilters, filters, columns, customFieldColumns } =
          value?.userQuery || {};
        update = {
          advancedFilters: advancedFilters?.length
            ? advancedFilters
            : undefined,
          filters,
          filterName: value.filterName,
        };

        if (hasColumnSelection) {
          update.columns = columns?.length ? columns : undefined;
          update.customFieldColumns = customFieldColumns?.length
            ? customFieldColumns
            : undefined;
        }
      }
      updateQuery(update);
      onChange(value);
    }
  };

  const handleInputChange = (e: React.SyntheticEvent, value: string) => {
    setSearchQuery(value);
  };

  const handleDelete = React.useCallback(
    (filter: SavedFilterType) => (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      openDialog(Dialogs.CONFIRMATION, {
        children: translate('CONSTITUENT_CONFIRM_REMOVE_FILTER', {
          filterName: filter.filterName,
        }),
        confirmLabel: translate('DELETE'),
        onCancel: closeDialog,
        onClose: closeDialog,
        onConfirm: () => {
          removeFilter(filter.savedFilterId);
          if (selectedFilter?.savedFilterId === filter.savedFilterId) {
            updateQuery({ filterName: null });
          }
          closeDialog();
        },
      });
    },
    [closeDialog, openDialog, removeFilter, selectedFilter, updateQuery],
  );

  if (!savedFilters && !searchQuery) return null;

  return (
    <Autocomplete
      id="saved-filter-select-label"
      className={className}
      options={savedFilters || []}
      getOptionLabel={(filter) => filter?.filterName || ''}
      renderOption={(props, filter) => (
        <li
          className="flex w-full items-center"
          {...props}
          key={filter.savedFilterId}
        >
          <div className="flex-grow truncate">{filter.filterName}</div>

          {(isOrgAdmin || user.userId === filter.userId) && (
            <CloseButton
              iconProps={{ className: 'text-grey-900 text-sm' }}
              label={translate('DELETE')}
              onClick={handleDelete(filter)}
              size="small"
            />
          )}
        </li>
      )}
      value={selectedFilter}
      inputValue={searchQuery}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translate('CONSTITUENT_SAVED_FILTERS')}
          inputProps={{ ...params.inputProps }}
        />
      )}
      onChange={handleChange}
      onInputChange={handleInputChange}
    />
  );
}

export default SavedFiltersSelect;
